/**
 * Formatting utility functions for display
 */

export type Units = 'metric' | 'imperial';

/**
 * Format a score with + prefix for positive values
 */
export function formatScore(score: number): string {
  return score > 0 ? `+${score}` : score.toString();
}

/**
 * Format distance in miles or kilometers based on units preference
 * Storage unit: km
 */
export function formatDistance(km: number | null, units: Units = 'imperial'): string {
  if (km === null) return '-';
  if (units === 'metric') {
    const rounded = Math.round(km);
    return rounded === 0 ? '<1 km' : `${rounded.toLocaleString()} km`;
  }
  const miles = Math.round(km * 0.621371);
  return miles === 0 ? '<1 mi' : `${miles.toLocaleString()} mi`;
}

/**
 * Format relative time ago from a date
 * Returns: <1m, 1m, 2m, ..., 1h, 2h, ..., 1d, 2d, ...
 */
export function formatTimeAgo(date: Date | string | null): string {
  if (!date) return '';
  const timestamp = typeof date === 'string' ? new Date(date).getTime() : date.getTime();
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return '<1m';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * Format height in feet and inches from centimeters
 * @deprecated Use formatHeight(cm, units) instead
 */
export function formatHeightImperial(cm: number): string {
  const feet = Math.floor(cm / 30.48);
  const inches = Math.round((cm % 30.48) / 2.54);
  return `${feet}'${inches}"`;
}

/**
 * Format height based on units preference
 * Storage unit: cm
 */
export function formatHeight(cm: number | null, units: Units = 'imperial'): string {
  if (cm === null) return '-';
  if (units === 'metric') {
    return `${Math.round(cm)} cm`;
  }
  const feet = Math.floor(cm / 30.48);
  const inches = Math.round((cm % 30.48) / 2.54);
  // Handle edge case where rounding gives 12 inches
  if (inches >= 12) {
    return `${feet + 1}'0"`;
  }
  return `${feet}'${inches}"`;
}

/**
 * Format weight based on units preference
 * Storage unit: lb
 */
export function formatWeight(lb: number | null, units: Units = 'imperial'): string {
  if (lb === null) return '-';
  if (units === 'metric') {
    const kg = Math.round(lb * 0.453592);
    return `${kg} kg`;
  }
  return `${Math.round(lb)} lb`;
}

/**
 * Calculate age from birth date with 2 decimal precision
 */
export function calculateAge(birthDate: string): number | null {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const now = new Date();
  const diffMs = now.getTime() - birth.getTime();
  const ageYears = diffMs / (1000 * 60 * 60 * 24 * 365.25);
  return Math.round(ageYears * 100) / 100;
}
